import { colors, intl, ui } from '@beelday/common'
import styled from '@emotion/styled'
import { AnsweredQuestion, PickResult } from '../model/knowledge-check-outcome'
import {
	Card,
	CardLabel,
	CardTitle,
	Confirmation,
	Eyebrow,
	Screen,
	ScreenReaderOnly,
	SentCheck,
	StateActions,
	Tick,
} from './primitives'

const ScoreBadge = styled.span`
	font-size: 24px;
	font-weight: 700;
	color: ${colors.indigoBlue};
`

const SeeAnswers = styled(ui.LinkButton)`
	font-size: 16px;
	font-weight: 700;
`

export type Score = { correct: number; total: number }

type SummaryProps = {
	/** Absent for a check without correct answers: nothing to score. */
	score?: Score
	onSeeAnswers: () => void
}

/**
 * Right after the last question: how it went, and the way into the answers.
 * The correct answers themselves wait for the trainer's review.
 */
export const KnowledgeCheckSummary = ({
	score,
	onSeeAnswers,
}: SummaryProps): JSX.Element => {
	const perfect = !!score && score.total > 0 && score.correct === score.total

	return (
		<Confirmation
			icon={
				score && !perfect ? (
					<ScoreBadge>{`${score.correct}/${score.total}`}</ScoreBadge>
				) : (
					<SentCheck />
				)
			}
			background={
				score && !perfect ? colors.indigoBlue10 : colors.greenishCyan02
			}
			title={
				!score ? (
					<intl.Translate>in_person.mobile.sent.answers_title</intl.Translate>
				) : perfect ? (
					<intl.Translate>
						in_person.mobile.knowledge_check.score.perfect
					</intl.Translate>
				) : (
					<intl.Translate correct={score.correct} total={score.total}>
						in_person.mobile.knowledge_check.score
					</intl.Translate>
				)
			}
			description={
				<intl.Translate>
					in_person.mobile.knowledge_check.summary.description
				</intl.Translate>
			}
			testId="in_person.mobile.knowledge_check.summary"
		>
			<StateActions>
				<SeeAnswers type="button" onClick={onSeeAnswers}>
					<intl.Translate>
						in_person.mobile.knowledge_check.see_answers
					</intl.Translate>
				</SeeAnswers>
			</StateActions>
		</Confirmation>
	)
}

const Back = styled.button`
	all: unset;
	align-self: flex-start;
	margin-bottom: 16px;
	color: ${colors.indigoBlue};
	font-size: 16px;
	font-weight: 700;
	cursor: pointer;
`

const Questions = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`

const AnswerCard = styled(Card)`
	padding: 0;
	overflow: hidden;
`

const AnswerCardHead = styled.div`
	padding: 20px 20px 12px;
	border-bottom: 1px solid ${colors.borderLightGray};
`

const Picks = styled.ul`
	margin: 0;
	padding: 16px 20px 20px;
	list-style: none;
	display: flex;
	flex-direction: column;
	gap: 12px;
`

const Pick = styled.li`
	display: flex;
	align-items: center;
	gap: 12px;
	min-height: 52px;
	padding: 12px 16px;
	border: 1px solid ${colors.borderLightGray};
	border-radius: 16px;
	background: ${colors.white};
	font-size: 14px;
	font-weight: 500;
	line-height: 1.4;
	color: ${colors.blackish};
	overflow-wrap: anywhere;
`

const PICK_COLOR: Record<PickResult, string> = {
	correct: colors.greenishCyan,
	incorrect: colors.lipstick,
	picked: colors.indigoBlue,
}

const PICK_LABEL: Partial<Record<PickResult, string>> = {
	correct: 'in_person.mobile.quiz.correct_answer',
	incorrect: 'in_person.mobile.quiz.wrong_answer',
}

const Mark = styled.span<{ $result: PickResult }>`
	width: 24px;
	height: 24px;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	background: ${props => PICK_COLOR[props.$result]};
`

const Cross = (): JSX.Element => (
	<svg width="10" height="10" viewBox="0 0 10 10" aria-hidden>
		<path
			d="M1.5 1.5l7 7M8.5 1.5l-7 7"
			stroke={colors.white}
			strokeWidth="1.6"
			strokeLinecap="round"
		/>
	</svg>
)

const NoAnswer = styled.li`
	font-size: 14px;
	color: ${colors.heather};
`

type AnswersProps = {
	questions: AnsweredQuestion[]
	/** Back to the score, or to "sent" for a check without one. */
	scored: boolean
	onBack: () => void
}

/**
 * What the participant picked, question by question. Each pick says whether
 * it was right; the options they did not pick are left out, so the correct
 * answer to a question they missed stays for the trainer's review.
 */
export const KnowledgeCheckAnswers = ({
	questions,
	scored,
	onBack,
}: AnswersProps): JSX.Element => {
	const translate = intl.useTranslate()

	return (
		<Screen data-testid="in_person.mobile.knowledge_check.answers">
			<Back type="button" onClick={onBack}>
				{'‹ '}
				<intl.Translate>
					{scored
						? 'in_person.mobile.knowledge_check.back_to_result'
						: 'in_person.mobile.back'}
				</intl.Translate>
			</Back>
			<Eyebrow>
				<intl.Translate>
					in_person.mobile.knowledge_check.your_answers
				</intl.Translate>
			</Eyebrow>
			<Questions>
				{questions.map((question, index) => (
					<AnswerCard key={question.id}>
						<AnswerCardHead>
							<CardLabel>
								<intl.Translate number={index + 1}>
									in_person.mobile.knowledge_check.question
								</intl.Translate>
							</CardLabel>
							<CardTitle>{question.question}</CardTitle>
						</AnswerCardHead>
						<Picks>
							{question.picks.length ? (
								question.picks.map(pick => {
									const label = PICK_LABEL[pick.result]
									return (
										<Pick key={pick.id}>
											<Mark $result={pick.result}>
												{pick.result === 'incorrect' ? <Cross /> : <Tick />}
											</Mark>
											<span>
												{pick.text}
												{label ? (
													<ScreenReaderOnly>{` — ${translate(
														label
													)}`}</ScreenReaderOnly>
												) : null}
											</span>
										</Pick>
									)
								})
							) : (
								<NoAnswer>
									<intl.Translate>
										in_person.mobile.knowledge_check.no_answer
									</intl.Translate>
								</NoAnswer>
							)}
						</Picks>
					</AnswerCard>
				))}
			</Questions>
		</Screen>
	)
}
